import React from 'react'

import { createPortal } from 'react-dom'

import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'

import { Modal, ModalProps } from '../lib/Modal'
import { ModalPortalProps as ArcadeModalPortalProps } from './ModalPortal/ModalPortal'
import { ModalPortal as ArcadeModalPortal } from './ModalPortal/ModalPortal'

type LegacyModalPortalProps = { rootId?: string } & ModalProps
type LegacyModalVariant = NonNullable<ModalProps['variant']>
type ModalPortalVariant = LegacyModalVariant | NonNullable<ArcadeModalPortalProps['variant']>
type ModalPortalProps = Omit<LegacyModalPortalProps & ArcadeModalPortalProps, 'variant'> & {
  variant?: ModalPortalVariant
}

const isLegacyModalVariant = (variant: ModalPortalVariant | undefined): variant is LegacyModalVariant =>
  variant === 'default' || variant === 'receipt' || variant === 'fullscreen'

// TODO remove this export after arcade migration is complete and export ModalPortal directly
export const ModalPortal = React.forwardRef<HTMLDivElement, ModalPortalProps>(
  ({ rootId = 'modal-root', ...props }, ref) => {
    const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('modal')

    if (isArcadeMigrationEnabled) {
      // Check if we have legacy props to transform
      const hasLegacyProps =
        'onBack' in props ||
        'onClose' in props ||
        'closable' in props ||
        'hideOverflow' in props ||
        'overflowScroll' in props ||
        'disableAnimation' in props ||
        'width' in props ||
        'dataTestId' in props

      if (hasLegacyProps) {
        const {
          onBack,
          onClose,
          closable = true,
          hideOverflow,
          overflowScroll,
          disableAnimation = false,
          width,
          variant,
          dataTestId,
          ...rest
        } = props as LegacyModalPortalProps & { variant?: ModalPortalVariant }

        const arcadeProps: Partial<ArcadeModalPortalProps> = {
          ...rest,
          variant,
          allowOverflow: !hideOverflow,
          allowContentScroll: overflowScroll,
          enableAnimation: !disableAnimation,
          maxWidth: width,
          'data-testid': dataTestId,
        }

        if (closable && onClose) {
          arcadeProps.closeButtonProps = { onClose }
        }

        if (onBack) {
          arcadeProps.backButtonProps = { onBack }
        }

        return <ArcadeModalPortal rootId={rootId} {...arcadeProps} ref={ref} />
      }

      // If no legacy props, cast directly
      return <ArcadeModalPortal rootId={rootId} {...props} ref={ref} />
    }

    const { variant, ...legacyProps } = props as LegacyModalPortalProps & { variant?: ModalPortalVariant }

    return createPortal(
      <Modal {...legacyProps} variant={isLegacyModalVariant(variant) ? variant : undefined} />,
      document.getElementById(rootId) as HTMLElement
    )
  }
)

ModalPortal.displayName = 'Shared ModalPortal'
